/**
 * /changelog — public release notes, sourced from `/app/memory/CHANGELOG.md`.
 *
 * Why: procurement teams and serious prospects check whether a vendor
 * ships. Surfacing the changelog publicly is a low-effort credibility
 * lift. The single markdown file is the source of truth — backend
 * serves it as-is, this page renders it.
 *
 * Filters out lines marked `<!-- private -->` on the backend so the
 * same file can hold internal notes safely.
 */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { History, Calendar } from "lucide-react";
import { PublicShell } from "@/components/PublicShell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmt(epoch) {
  if (!epoch) return null;
  const d = new Date(epoch * 1000);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

export default function Changelog() {
  const [markdown, setMarkdown] = useState("");
  const [lastModified, setLastModified] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/changelog`)
      .then((r) => {
        setMarkdown(r.data.markdown || "");
        setLastModified(r.data.last_modified_epoch);
      })
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  return (
    <PublicShell>
      <Helmet>
        <title>Changelog — ERIO</title>
        <meta
          name="description"
          content="Release notes for the ERIO platform — what's shipped, dated, and tested."
        />
      </Helmet>

      <div className="mx-auto max-w-3xl px-6 py-12" data-testid="changelog-page">
        <header className="mb-8 flex items-start gap-3">
          <div className="rounded-xl bg-ek-purple/10 p-2.5 text-ek-purple">
            <History size={20} />
          </div>
          <div className="flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ek-purple">
              ERIO Programme
            </p>
            <h1 className="mt-2 font-display text-4xl font-medium tracking-[-0.025em] text-ek-ink sm:text-5xl">
              Changelog
            </h1>
            <p className="mt-3 max-w-2xl text-base font-light text-ek-ink/70">
              What we&apos;ve shipped — dated, tested, and version-controlled. The same file we
              keep internally; you&apos;re seeing the source of truth.
            </p>
            {lastModified && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-ek-ink/45"
                 data-testid="changelog-last-modified">
                <Calendar size={11} /> Updated {fmt(lastModified)}
              </p>
            )}
          </div>
        </header>

        {err && (
          <p className="rounded-md bg-rose-50 p-4 text-sm text-rose-700"
             data-testid="changelog-error">
            Failed to load changelog: {String(err)}
          </p>
        )}

        {!err && !markdown && <p className="text-sm text-ek-ink/55">Loading…</p>}

        {markdown && (
          <article
            className="prose prose-slate max-w-none
                       prose-headings:font-display prose-headings:tracking-[-0.015em]
                       prose-h1:hidden prose-h2:mt-10 prose-h2:text-xl prose-h2:font-semibold prose-h2:text-ek-ink
                       prose-h3:mt-6 prose-h3:text-base prose-h3:font-semibold prose-h3:text-ek-ink
                       prose-p:text-[15px] prose-p:font-light prose-p:leading-relaxed prose-p:text-ek-ink/75
                       prose-li:text-[15px] prose-li:font-light prose-li:text-ek-ink/75
                       prose-code:rounded prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5
                       prose-code:text-[12px] prose-code:before:content-none prose-code:after:content-none
                       prose-a:text-ek-purple prose-a:no-underline hover:prose-a:underline
                       prose-strong:text-ek-ink prose-strong:font-semibold
                       prose-table:text-sm prose-th:bg-slate-50 prose-th:text-ek-ink/70"
            data-testid="changelog-content"
          >
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </article>
        )}
      </div>
    </PublicShell>
  );
}
